import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";

/* Auth */
import { useAuth } from "./features/auth/context/AuthProvider";
import ProtectedRoute from "./features/auth/ProtectedRoute";

/* Páginas principais */
import LoginPage from "./features/login/ui/LoginPage";
import Home from "./features/home/Home";

/* Bases */
import AtividadesPage from "./features/bases/atividades/AtividadesPage";
import DesviosPage from "./features/bases/desvios/DesviosPage";
import DesviosEditarPage from "./features/bases/desvios_editar/DesviosEditarPage"; // ✅ ADD
import DesviosNegarPage from "./features/bases/desvios_negar/DesviosNegarPage";
import EmailsPage from "./features/bases/emails/EmailsPage";
import AutomacaoEmailsPage from "./features/automacaodeemails/AutomacaoEmailsPage";
import AtivosPage from "./features/bases/ativos/AtivosPage";
import EquipamentosPage from "./features/bases/equipamentos/EquipamentosPage";
import SboePage from "./features/bases/sb-origem-encerramento/SboePage";

/* Execução */
import { ExecucaoPage } from "./features/execucao";

/* Programações > WF1 */
import W1Page from "./features/wf1/components/W1Page";
import PlanejamentoMensalPage from "./features/planejamentomensal/PlanejamentoMensalPage";
import AjusteMensalPage from "./features/ajustemensal/AjusteMensalPage";
import ContratoMensalPage from "./features/contratomensal/ContratoMensalPage";

/* Intervenções */
import { ReaberturaPage } from "./features/reabertura";

/* Programações > WF1 Edição */
import W1EdicaoPage from "./features/w1edicao/components/page";

/* Programações > WF2 (D1 Calendário) */
import W2Page from "./features/wf2/components/W2Page";

/* Programações > WF3 */
import { W3Page } from "./features/wf3";

/* Status Diário */
import StatusDiarioContainer from "./features/statusdiario/StatusDiarioContainer";

/* ✅ NOVO: Programação D+1 */
import ProgramacaoDmais1Page from "./features/programacaodmais1/ProgramacaoDmais1Page";
import InterdicoesCimPage from "./features/cim/interdicoescim/InterdicoesCimPage";
import InterdicoesCcoPage from "./features/cim/interdicoescco/InterdicoesCcoPage";
import BasesCimPage from "./features/cim/bases/BasesCimPage";
import StatusCimContainer from "./features/cim/status/StatusCimContainer";
import RelatorioDiarioPage from "./features/relatoriodiario/RelatorioDiarioPage";
import { SimuladorWorkbenchPage } from "./features/simuladorWorkbench";
import CadastroTrilhosNovosPage from "./features/estaleiro/cadastroTrilhosNovos/CadastroTrilhosNovosPage";
import CadastroTrilhosReempregoPage from "./features/estaleiro/cadastroTrilhosReemprego/CadastroTrilhosReempregoPage";
import DepositoTrilhosPage from "./features/estaleiro/depositoTrilhos/DepositoTrilhosPage";
import BasesEstaleiroPage from "./features/bases-estaleiro/BasesEstaleiroPage";

/* ========= Helpers ========= */

function getBasename() {
  const vite =
    typeof import.meta !== "undefined" && import.meta.env?.VITE_BASENAME;
  const cra = process.env?.REACT_APP_BASENAME;
  const raw = (vite ?? cra ?? "").trim();
  if (!raw || raw === "/") return "";
  return raw.replace(/\/+$/, "");
}

function HashMigration() {
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!loc.hash) return;
    const raw = loc.hash.slice(1).trim();
    if (!raw) return;

    const id = raw.startsWith("view=") ? raw.slice(5) : raw.split("?")[0];
    const qs = raw.includes("?") ? `?${raw.split("?")[1]}` : "";

    const map = {
      home: "/",
      execucao: "/execucao",
      atividades: "/bases/atividades",
      desvios: "/bases/desvios",
      emails: "/bases/emails",
      automacaoEmails: "/automacao-emails",
      ativos: "/bases/ativos",
      equipamentos: "/bases/equipamentos",
      sboe: "/bases/sb-origem-encerramento",
      sb_origem_encerramento: "/bases/sb-origem-encerramento",
      sborigemencerramento: "/bases/sb-origem-encerramento",

      // ✅ NOVO: Desvios (Edição)
      desviosedicao: "/bases/desvios/edicao",
      desvioedicao: "/bases/desvios/edicao",
      desvios_edit: "/bases/desvios/edicao",
      desviosed: "/bases/desvios/edicao",
      desviosnegar: "/bases/desvios/negar",
      desvio_negar: "/bases/desvios/negar",
      desvios_cancelamento: "/bases/desvios/negar",

      // WF2 (D1 Calendário)
      d1: "/programacoes/d1",
      d1calendario: "/programacoes/d1",
      programacaoD1: "/programacoes/d1",

      // WF1
      planejamento: "/programacoes/planejamento",
      planejamentoMensal: "/programacoes/planejamento-mensal",
      planejamento_mensal: "/programacoes/planejamento-mensal",
      planejamentoMensalAjuste: "/programacoes/planejamento-mensal/ajuste",
      planejamento_mensal_ajuste: "/programacoes/planejamento-mensal/ajuste",
      planejamentoMensalContrato: "/programacoes/planejamento-mensal/contrato",
      planejamento_mensal_contrato: "/programacoes/planejamento-mensal/contrato",

      // WF3 e Execução Dia
      wf3: "/programacoes/wf3",
      execucaodia: "/programacoes/execucao-dia",

      // Intervenções
      reabertura: "/reabertura",

      // Status Diário (hash legado)
      statusdiario: "/status-diario",

      // ✅ NOVO: D+1
      dmais1: "/utilitarios/programacao-dmais1",
      programacaodmais1: "/utilitarios/programacao-dmais1",
      dplus1: "/utilitarios/programacao-dmais1",
      interdicoes: "/cim/interdicoes",
      telaInterdicoes: "/cim/interdicoes",
      tela_interdicoes: "/cim/interdicoes",
      ciminterdicoes: "/cim/interdicoes",
      interdicoescim: "/cim/interdicoes",
      ciminterdicoescim: "/cim/interdicoes",
      statuscim: "/cim/status",
      cimstatus: "/cim/status",
      cim_status: "/cim/status",
      interdicoescco: "/cim/interdicoescco",
      tela_interdicoes_cco: "/cim/interdicoescco",
      ciminterdicoescco: "/cim/interdicoescco",

      relatoriodiario: "/utilitarios/relatorio-diario",
      relatorio_diario: "/utilitarios/relatorio-diario",
      simuladorworkbench: "/utilitarios/simulador-workbench",
      simulador_workbench: "/utilitarios/simulador-workbench",
      cadastrotrilhosnovos: "/estaleiro/cadastro-trilhos-novos",
      cadastro_trilhos_novos: "/estaleiro/cadastro-trilhos-novos",
      cadastrotrilhosreemprego: "/estaleiro/cadastro-trilhos-reemprego",
      cadastro_trilhos_reemprego: "/estaleiro/cadastro-trilhos-reemprego",
      depositotrilhos: "/estaleiro/deposito-trilhos",
      deposito_trilhos: "/estaleiro/deposito-trilhos",
      manutencaoestaleiro: "/estaleiro/manutencao-estaleiro",
      manutencao_estaleiro: "/estaleiro/manutencao-estaleiro",
      manutencaocim: "/cim/manutencao-cim",
      manutencao_cim: "/cim/manutencao-cim",
      basescim: "/cim/manutencao-cim",
      bases_cim: "/cim/manutencao-cim",
    };

    const target = map[id];
    if (target) navigate(`${target}${qs}`, { replace: true });
  }, [loc.hash, navigate]);

  return null;
}

function resolveComponent(C) {
  if (typeof C === "function") return C;
  if (C && typeof C.default === "function") return C.default;
  if (C && typeof C === "object") {
    const maybe = Object.values(C).find((v) => typeof v === "function");
    if (maybe) return maybe;
  }
  return null;
}

function ProtectedShell() {
  return (
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  );
}

function WithBack({ Component, to = "/" }) {
  const navigate = useNavigate();
  const C = resolveComponent(Component);
  if (!C) {
    console.error("[WithBack] Componente inválido:", Component);
    return <div style={{ padding: 24 }}>Componente inválido.</div>;
  }
  const handleBack = () => {
    navigate({ pathname: to, search: "", hash: "" }, { replace: to === "/" });
  };
  return <C onBack={handleBack} />;
}

function WithBackGoBack({ Component }) {
  const navigate = useNavigate();
  const C = resolveComponent(Component);
  if (!C) {
    console.error("[WithBackGoBack] Componente inválido:", Component);
    return <div style={{ padding: 24 }}>Componente inválido.</div>;
  }
  return <C onBack={() => navigate(-1)} />;
}

function RedirectIfAuthed({ children }) {
  const { booting, isAuthenticated } = useAuth();
  const location = useLocation();

  if (booting) return null;
  if (isAuthenticated) {
    const to = location.state?.from?.pathname || "/";
    return <Navigate to={to} replace />;
  }
  return children;
}

/** Home com callbacks de navegação */
function HomeWithNav() {
  const navigate = useNavigate();
  return (
    <Home
      onOpenPlanejamento={() => navigate("/programacoes/planejamento")}
      onOpenPlanejamentoEdicao={() => navigate("/programacoes/planejamento/edicao")}
      onOpenPlanejamentoMensal={() => navigate("/programacoes/planejamento-mensal")}
      onOpenPlanejamentoMensalAjuste={() => navigate("/programacoes/planejamento-mensal/ajuste")}
      onOpenPlanejamentoMensalContrato={() => navigate("/programacoes/planejamento-mensal/contrato")}
      onOpenAtividades={() => navigate("/bases/atividades")}
      onOpenDesvios={() => navigate("/bases/desvios")}

      // ✅ NOVO: Desvios (Edição)
      onOpenDesviosEdicao={() => navigate("/bases/desvios/edicao")}
      onOpenDesviosNegar={() => navigate("/bases/desvios/negar")}

      onOpenEmails={() => navigate("/bases/emails")}
      onOpenAutomacaoEmails={() => navigate("/automacao-emails")}
      onOpenAtivos={() => navigate("/bases/ativos")}
      onOpenEquipamentos={() => navigate("/bases/equipamentos")}
      onOpenSboe={() => navigate("/bases/sb-origem-encerramento")}
      onOpenExecucao={() => navigate("/execucao")}
      onOpenReabertura={() => navigate("/reabertura")}
      onOpenD1Calendario={() => navigate("/programacoes/d1")}              // WF2
      onOpenWF3={() => navigate("/programacoes/wf3")}                      // WF3
      onOpenExecucaoDia={() => navigate("/programacoes/execucao-dia")}     // Execução Dia
      onOpenStatusDiario={() => navigate("/status-diario")}                // Status Diário
      onOpenRelatorioDiario={() => navigate("/utilitarios/relatorio-diario")}

      // ✅ NOVO: Programação D+1
      onOpenProgramacaoDmais1={() => navigate("/utilitarios/programacao-dmais1")}
      onOpenCimInterdicoes={() => navigate("/cim/interdicoes")}
      onOpenCimStatus={() => navigate("/cim/status")}
      onOpenCimInterdicoesCco={() => navigate("/cim/interdicoescco")}
      onOpenSimuladorWorkbench={() => navigate("/utilitarios/simulador-workbench")}
      onOpenCadastroTrilhosNovos={() => navigate("/estaleiro/cadastro-trilhos-novos")}
      onOpenCadastroTrilhosReemprego={() => navigate("/estaleiro/cadastro-trilhos-reemprego")}
      onOpenDepositoTrilhos={() => navigate("/estaleiro/deposito-trilhos")}
      onOpenManutencaoEstaleiro={() => navigate("/estaleiro/manutencao-estaleiro")}
      onOpenManutencaoCim={() => navigate("/cim/manutencao-cim")}
    />
  );
}

/* ========= App ========= */

export default function App() {
  const { booting } = useAuth();
  const BASENAME = getBasename();

  if (booting) {
    return (
      <div style={{ height: "100vh", display: "grid", placeItems: "center" }}>
        Carregando…
      </div>
    );
  }

  return (
    <BrowserRouter basename={BASENAME}>
      <HashMigration />

      <Routes>
        {/* Login (público) */}
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <LoginPage />
            </RedirectIfAuthed>
          }
        />

        {/* Área protegida */}
        <Route element={<ProtectedShell />}>
          {/* Home */}
          <Route index element={<HomeWithNav />} />

          {/* Execução */}
          <Route
            path="execucao/*"
            element={<WithBack Component={ExecucaoPage} to="/" />}
          />

          {/* Status Diário */}
          <Route
            path="status-diario"
            element={<WithBack Component={StatusDiarioContainer} to="/" />}
          />

          {/* ✅ NOVO: Utilitários > Programação D+1 */}
          <Route
            path="utilitarios/programacao-dmais1"
            element={<WithBack Component={ProgramacaoDmais1Page} to="/" />}
          />

          <Route
            path="cim/status"
            element={<WithBack Component={StatusCimContainer} to="/" />}
          />

          <Route
            path="cim/interdicoes"
            element={<WithBack Component={InterdicoesCimPage} to="/" />}
          />

          <Route
            path="cim/interdicoescco"
            element={<WithBack Component={InterdicoesCcoPage} to="/" />}
          />

          <Route
            path="cim/manutencao-cim"
            element={<WithBack Component={BasesCimPage} to="/" />}
          />

          <Route
            path="interdicoes"
            element={<Navigate to="/cim/interdicoes" replace />}
          />

          <Route
            path="interdicoes/numeros"
            element={<Navigate to="/cim/interdicoescco" replace />}
          />

          <Route
            path="cim/bases"
            element={<Navigate to="/cim/manutencao-cim" replace />}
          />

          <Route
            path="utilitarios/relatorio-diario"
            element={<WithBack Component={RelatorioDiarioPage} to="/" />}
          />

          <Route
            path="utilitarios/simulador-workbench"
            element={<WithBack Component={SimuladorWorkbenchPage} to="/" />}
          />

          <Route
            path="estaleiro/cadastro-trilhos-novos"
            element={<WithBack Component={CadastroTrilhosNovosPage} to="/" />}
          />

          <Route
            path="estaleiro/cadastro-trilhos-reemprego"
            element={<WithBack Component={CadastroTrilhosReempregoPage} to="/" />}
          />

          <Route
            path="estaleiro/deposito-trilhos"
            element={<WithBack Component={DepositoTrilhosPage} to="/" />}
          />

          <Route
            path="estaleiro/manutencao-estaleiro"
            element={<WithBack Component={BasesEstaleiroPage} to="/" />}
          />

          {/* Programações > D1 (Calendário) — WF2 */}
          <Route
            path="programacoes/d1"
            element={<WithBack Component={W2Page} to="/" />}
          />

          {/* Programações > WF-3 */}
          <Route
            path="programacoes/wf3"
            element={<WithBack Component={W3Page} to="/" />}
          />

          {/* Programações > Execução Dia (mesma W3Page) */}
          <Route
            path="programacoes/execucao-dia"
            element={<WithBack Component={W3Page} to="/" />}
          />

          {/* Programações > Planejamento Mensal/Semanal (WF1) */}
          <Route
            path="programacoes/planejamento"
            element={<WithBack Component={W1Page} to="/" />}
          />

          <Route
            path="programacoes/planejamento-mensal"
            element={<WithBack Component={PlanejamentoMensalPage} to="/" />}
          />

          <Route
            path="programacoes/planejamento-mensal/ajuste"
            element={<WithBack Component={AjusteMensalPage} to="/" />}
          />

          <Route
            path="programacoes/planejamento-mensal/contrato"
            element={<WithBack Component={ContratoMensalPage} to="/" />}
          />

          {/* Programações > Visualização/Edição Mensal/Semanal (WF1 Edição) */}
          <Route
            path="programacoes/planejamento/edicao"
            element={<WithBack Component={W1EdicaoPage} to="/" />}
          />

          {/* Bases */}
          <Route
            path="bases/atividades"
            element={<WithBack Component={AtividadesPage} to="/" />}
          />
          <Route
            path="bases/desvios"
            element={<WithBack Component={DesviosPage} to="/" />}
          />

          {/* ✅ AQUI: rota certa para a tabela certa (/desvios_editar) */}
          <Route
            path="bases/desvios/edicao"
            element={<WithBack Component={DesviosEditarPage} to="/" />}
          />
          <Route
            path="bases/desvios/negar"
            element={<WithBack Component={DesviosNegarPage} to="/" />}
          />

          <Route
            path="bases/emails"
            element={<WithBack Component={EmailsPage} to="/" />}
          />
          <Route
            path="bases/ativos"
            element={<WithBack Component={AtivosPage} to="/" />}
          />
          <Route
            path="bases/equipamentos"
            element={<WithBack Component={EquipamentosPage} to="/" />}
          />
          <Route
            path="bases/sb-origem-encerramento"
            element={<WithBack Component={SboePage} to="/" />}
          />

          {/* Automação de Emails (fora de /bases) */}
          <Route
            path="automacao-emails/*"
            element={<WithBackGoBack Component={AutomacaoEmailsPage} />}
          />

          {/* Intervenções */}
          <Route
            path="reabertura"
            element={<WithBack Component={ReaberturaPage} to="/" />}
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

